/**
 * Product Service
 * API calls for product management
 */

import api from './api.js';
import type { ApiResponse, PaginatedResponse } from '@/types/api.js';
import type {
  Product,
  ProductCreateInput,
  ProductUpdateInput,
  ProductListParams,
  ProductStatistics,
  ProductCategory,
  InventoryAdjustment,
} from '@/types/product.js';

export const productsService = {
  /**
   * Get paginated list of products
   */
  async list(params: ProductListParams = {}): Promise<PaginatedResponse<Product>> {
    const response = await api.get<ApiResponse<PaginatedResponse<Product>>>('/products', {
      params,
    });
    return response.data.data;
  },

  /**
   * Get product by ID
   */
  async getById(id: string): Promise<Product> {
    const response = await api.get<ApiResponse<Product>>(`/products/${id}`);
    return response.data.data;
  },

  /**
   * Create new product
   */
  async create(data: ProductCreateInput): Promise<Product> {
    const response = await api.post<ApiResponse<Product>>('/products', data);
    return response.data.data;
  },

  /**
   * Update product
   */
  async update(id: string, data: ProductUpdateInput): Promise<Product> {
    const response = await api.put<ApiResponse<Product>>(`/products/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete product
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/products/${id}`);
  },

  /**
   * Update product status
   */
  async updateStatus(
    id: string,
    status: 'active' | 'inactive' | 'discontinued'
  ): Promise<Product> {
    const response = await api.patch<ApiResponse<Product>>(`/products/${id}/status`, {
      status,
    });
    return response.data.data;
  },

  /**
   * Bulk delete products
   */
  async bulkDelete(ids: string[]): Promise<void> {
    await api.post('/products/bulk-delete', { ids });
  },

  /**
   * Get product statistics
   */
  async getStatistics(): Promise<ProductStatistics> {
    const response = await api.get<ApiResponse<ProductStatistics>>('/products/statistics');
    return response.data.data;
  },

  /**
   * Adjust inventory
   */
  async adjustInventory(productId: string, data: InventoryAdjustment): Promise<Product> {
    const response = await api.post<ApiResponse<Product>>(
      `/products/${productId}/inventory`,
      data
    );
    return response.data.data;
  },

  // Categories

  /**
   * Get product categories
   */
  async getCategories(): Promise<ProductCategory[]> {
    const response = await api.get<ApiResponse<ProductCategory[]>>('/products/categories');
    return response.data.data;
  },

  /**
   * Create product category
   */
  async createCategory(data: { name: string; description?: string; parentId?: string }): Promise<ProductCategory> {
    const response = await api.post<ApiResponse<ProductCategory>>('/products/categories', data);
    return response.data.data;
  },

  /**
   * Update product category
   */
  async updateCategory(
    id: string,
    data: Partial<{ name: string; description: string; parentId: string }>
  ): Promise<ProductCategory> {
    const response = await api.put<ApiResponse<ProductCategory>>(
      `/products/categories/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete product category
   */
  async deleteCategory(id: string): Promise<void> {
    await api.delete(`/products/categories/${id}`);
  },
};

export default productsService;
